import React, { useMemo, useState } from 'react'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { SingleSelect, RichTextInput, TextInput, NumberInput } from 'uiComponents/input'
import { ImageUploadResponse } from 'http/imagesService'
import cloneDeep from 'lodash/cloneDeep'
import { SecondaryText, Body } from 'uiComponents/typography'
import {
    ProductListData,
    Locale,
    CMS_KEY_PLACEHOLDER,
    CheckoutFlow,
    ConfirmationFlow,
    CheckoutFlowNameMap,
} from 'admin/articleService'
import { UploadInput } from 'uiComponents/input/upload'
import { MessageKind } from 'uiComponents/messages'
import Infotip, { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { Tabs } from 'uiComponents/navigation/tabs'
import { Col, Row } from 'uiComponents/flex'
import { DeleteNote, UploadExplanation, ImageUrlCol, ImgLink } from '../common'
import { FormTogglerCol, FormToggler } from 'uiComponents/input/toggle'
import { useGetAllCategoriesForSelect, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { extractContentFromHTML } from '../utils'
import { ComboboxField } from 'uiComponents/combobox/combobox'
import { t, useTranslation, Trans } from 'translations'

interface GeneralInfoSectionProps {
    accountSlug: string
    locales: Locale[]
    activeLocaleIndex: number
    setActiveLocaleIndex: (index: number) => void
    localesWithMissingName: string[]
    localesWithMissingDescription: string[]
    localesWithMissingTimeSlotInfo: string[]
    showBookingLimitsError: boolean
    updateData: (data: ProductListData) => void
    categoryData: ProductListData
    onWideImageUpload: (file: File) => Promise<any>
    onSquareImageUpload: (file: File) => Promise<any>
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
    onWideImageUploadSuccess: (response: ImageUploadResponse) => void
    onSquareImageUploadSuccess: (response: ImageUploadResponse) => void
    hasFeature: (feature: string, slug: string) => boolean
    viewMode: 'flat' | 'nested' | ''
}

const categoryFlowOptions: { name: string; value: CheckoutFlow }[] = [
    { value: 'dated', name: CheckoutFlowNameMap.dated },
    { value: 'undated', name: CheckoutFlowNameMap.undated },
    { value: 'rtp', name: CheckoutFlowNameMap.rtp },
    { value: 'date_change', name: CheckoutFlowNameMap.date_change },
    { value: 'membership', name: CheckoutFlowNameMap.membership },
]

const getConfirmationFlowOptionsList = (): { name: string; value: ConfirmationFlow }[] => [
    { name: t('products.crud.categories.generalInfo.confirmationFlows.paymentRequired'), value: 'payment_required' },
    { name: t('products.crud.categories.generalInfo.confirmationFlows.reservation'), value: 'reservation' },
    {
        name: t('products.crud.categories.generalInfo.confirmationFlows.validityExtension'),
        value: 'validity_extension',
    },
    { name: t('products.crud.categories.generalInfo.confirmationFlows.ruhrTopcardCheck'), value: 'ruhr_topcard_check' },
]

const noReservationCheckoutFlows: CheckoutFlow[] = ['undated', 'date_change', 'membership']

const getReservationOption = () => ({
    name: t('products.crud.categories.generalInfo.dateReservation'),
    value: 'date_reservation',
})

let isForGroupBookingsWasTurnedOffIndirectly = false

export function GeneralInfoSection(props: GeneralInfoSectionProps) {
    const { t } = useTranslation()
    const {
        activeLocaleIndex,
        setActiveLocaleIndex,
        localesWithMissingName,
        localesWithMissingDescription,
        localesWithMissingTimeSlotInfo,
        showBookingLimitsError,
        locales,
        categoryData,
        updateData,
    } = props
    const categoriesForSelect = useGetAllCategoriesForSelect()
    const isLoadingCategoriesOrArticles = useIsLoadingCategoriesOrArticles()
    const [descriptionInvalidAmountOfCharsError, setDescriptionInvalidAmountOfCharsError] = useState<boolean>(false)
    const newData = useMemo(() => cloneDeep(categoryData), [categoryData])

    function onTitleChange(event: React.ChangeEvent<HTMLInputElement>, locale: Locale) {
        newData.title.text[locale.code] = event.target.value
        updateData(newData)
    }

    function onDescriptionChange(value: string, locale: Locale) {
        setDescriptionInvalidAmountOfCharsError(false)
        if (newData.imageType === 'square_image') {
            setDescriptionInvalidAmountOfCharsError(extractContentFromHTML(value).length >= 120)
        }

        if (newData.description) {
            newData.description.text[locale.code] = value
        } else {
            newData.description = {
                key: CMS_KEY_PLACEHOLDER,
                text: {
                    [locale.code]: value,
                },
            }
        }
    }

    function onTimeSlotInfoChange(value: string, locale: Locale) {
        if (newData.timeSlotInfo) {
            newData.timeSlotInfo.text[locale.code] = value
        } else {
            newData.timeSlotInfo = {
                key: CMS_KEY_PLACEHOLDER,
                text: {
                    [locale.code]: value,
                },
            }
        }
    }

    function onParentIdChange(value: string) {
        newData.parentId = (value || '').trim() === '' ? null : value
        updateData(newData)
    }

    function onPriorityChange(event: React.ChangeEvent<HTMLInputElement>) {
        newData.priority = event.target.value === '' ? null : +event.target.value
        updateData(newData)
    }

    function onPriorityBlur() {
        if (categoryData.priority !== null) {
            return
        }
        newData.priority = 1
        updateData(newData)
    }

    function onFlowChange(flow: CheckoutFlow) {
        newData.flow = flow
        if (!['dated', 'rtp'].includes(flow)) {
            if (newData.isForGroupBookings) {
                newData.isForGroupBookings = false
                // ensure we switch isForGroupBookings setting back on if it was indirectly turned off by this toggle
                isForGroupBookingsWasTurnedOffIndirectly = true
            }
        } else if (isForGroupBookingsWasTurnedOffIndirectly) {
            newData.isForGroupBookings = true
            isForGroupBookingsWasTurnedOffIndirectly = false
        }
        if (noReservationCheckoutFlows.includes(flow) && newData.confirmationFlow === 'reservation') {
            newData.confirmationFlow = 'payment_required'
        }
        updateData(newData)
    }

    function onConfirmationFlowChange(flow: ConfirmationFlow) {
        newData.confirmationFlow = flow
        updateData(newData)
    }

    function onCardOpenChange(value: boolean) {
        newData.cardOpenByDefault = value
        updateData(newData)
    }

    function onBookingLimitsChange(
        event: React.ChangeEvent<HTMLInputElement>,
        field: 'bookingHorizon' | 'releaseTime',
    ) {
        newData[field] = event.target.value === '' ? null : +event.target.value
        updateData(newData)
    }

    function removeWideCategoryImage() {
        newData.image = null
        updateData(newData)
    }

    function removeSquareCategoryImage() {
        newData.squareImage = null
        updateData(newData)
    }

    function getCheckoutFlowOptions() {
        if (newData.flow === 'date_reservation') {
            return [getReservationOption()]
        }

        const includeDateChange = props.hasFeature('DateChangeProductCRUDUpdate', props.accountSlug)
        const includeMembership = props.hasFeature('memberships', props.accountSlug)

        return categoryFlowOptions.filter((flow) => {
            if (flow.value === 'date_change' && !includeDateChange) {
                return false
            }
            if (flow.value === 'membership' && !includeMembership) {
                return false
            }
            return true
        })
    }

    function getConfirmationFlowOptions() {
        const edgeCaseConfFlows: ConfirmationFlow[] = ['validity_extension', 'ruhr_topcard_check']
        const confirmationFlowOptions = getConfirmationFlowOptionsList()
        let options = props.hasFeature('ShowEdgeCaseConfirmationFlows', props.accountSlug)
            ? confirmationFlowOptions
            : confirmationFlowOptions.filter((o) => !edgeCaseConfFlows.includes(o.value))
        if (newData.flow && noReservationCheckoutFlows.includes(newData.flow)) {
            options = options.filter((o) => o.value !== 'reservation')
        }
        return options
    }

    const reservationFlow = newData.flow === 'date_reservation'
    return (
        <div>
            <FormItem htmlFor="checkout-flow" style={{ position: 'relative', margin: '1em 0' }}>
                <FormItemName>{t('products.crud.categories.generalInfo.checkoutFlowType')}</FormItemName>
                <DisabledItemHoverInfotip
                    active={reservationFlow}
                    infotipText={t('products.crud.categories.generalInfo.dateReservationInfotip')}
                    width="30em"
                >
                    <SingleSelect
                        id="checkout-flow"
                        options={getCheckoutFlowOptions()}
                        selected={newData.flow || ''}
                        height="2.8em"
                        maxHeight="11em"
                        noSelectOption={t('products.crud.categories.generalInfo.none')}
                        onSelect={onFlowChange}
                        disabled={reservationFlow}
                    />
                </DisabledItemHoverInfotip>
            </FormItem>
            <FormItem htmlFor="confirmation-flow" style={{ margin: '1.5em 0' }}>
                <FormItemName>{t('products.crud.categories.generalInfo.confirmationFlowType')}</FormItemName>
                <SingleSelect
                    id="confirmation-flow"
                    options={getConfirmationFlowOptions()}
                    selected={newData.confirmationFlow || ''}
                    height="2.8em"
                    maxHeight="11em"
                    noSelectOption={t('products.crud.categories.generalInfo.none')}
                    onSelect={onConfirmationFlowChange}
                />
            </FormItem>
            <Tabs
                titles={locales.map((locale) => locale.name)}
                setActive={setActiveLocaleIndex}
                activeTabIndex={activeLocaleIndex}
            >
                {locales.map((locale, index) => {
                    return (
                        <div key={index}>
                            <FormItem htmlFor="category-name" style={{ position: 'relative', marginBottom: 24 }}>
                                <FormItemName>
                                    {t('products.crud.categories.generalInfo.categoryName')}
                                    <Infotip pointer="left">
                                        {t('products.crud.categories.generalInfo.categoryNameTooltip')}
                                    </Infotip>
                                </FormItemName>
                                <TextInput
                                    id={`category-name-${[locale.code]}`}
                                    name="category-name"
                                    type="text"
                                    maxLength={50}
                                    value={newData.title?.text[locale.code] || ''}
                                    status={localesWithMissingName.includes(locale.code) ? 'error' : 'normal'}
                                    placeholder={t('products.crud.categories.generalInfo.categoryNamePlaceholder')}
                                    onChange={(event) => onTitleChange(event, locale)}
                                    locale={locale.code}
                                    block
                                />
                            </FormItem>
                            {localesWithMissingName.includes(locale.code) && (
                                <ValidationMessage className="validation-message-visible" style={{ top: '-1.8em' }}>
                                    {t('products.crud.categories.generalInfo.fieldRequired')}
                                </ValidationMessage>
                            )}
                            <FormItem htmlFor="category-description" style={{ position: 'relative' }}>
                                <div style={{ display: 'flex' }}>
                                    <FormItemName>
                                        {t('products.crud.categories.generalInfo.categoryDescription')}
                                    </FormItemName>
                                </div>
                                <RichTextInput
                                    id={`category-description-${[locale.code]}`}
                                    onChange={(value) => onDescriptionChange(value, locale)}
                                    onBlur={() => updateData(newData)}
                                    value={newData.description?.text[locale.code] || ''}
                                    maxLength={300}
                                    showCharsLeft
                                    status={
                                        localesWithMissingDescription.includes(locale.code)
                                            ? 'error'
                                            : descriptionInvalidAmountOfCharsError
                                            ? 'warning'
                                            : 'normal'
                                    }
                                    warningMessage={t(
                                        'products.crud.categories.generalInfo.descriptionTooLongForSquare',
                                    )}
                                />
                            </FormItem>
                            {localesWithMissingDescription.includes(locale.code) && (
                                <ValidationMessage className="validation-message-visible" style={{ top: '-1.8em' }}>
                                    {t('products.crud.categories.generalInfo.allDescriptionTranslationsRequired')}
                                </ValidationMessage>
                            )}
                            {newData.isLeaf && newData.timeSlotsEnabled && (
                                <>
                                    <FormItem htmlFor="category-time-slot-info" style={{ position: 'relative' }}>
                                        <div style={{ display: 'flex' }}>
                                            <FormItemName>
                                                {t('products.crud.categories.generalInfo.timeSlotInfo')}
                                            </FormItemName>
                                        </div>
                                        <RichTextInput
                                            id={`category-time-slot-${[locale.code]}`}
                                            onChange={(value) => onTimeSlotInfoChange(value, locale)}
                                            onBlur={() => updateData(newData)}
                                            value={newData.timeSlotInfo?.text[locale.code] || ''}
                                            maxLength={500}
                                            showCharsLeft
                                            status={
                                                localesWithMissingTimeSlotInfo.includes(locale.code)
                                                    ? 'error'
                                                    : 'normal'
                                            }
                                        />
                                    </FormItem>
                                    {localesWithMissingTimeSlotInfo.includes(locale.code) && (
                                        <ValidationMessage
                                            className="validation-message-visible"
                                            style={{ top: '-1.8em' }}
                                        >
                                            {t(
                                                'products.crud.categories.generalInfo.allTimeSlotInfoTranslationsRequired',
                                            )}
                                        </ValidationMessage>
                                    )}
                                </>
                            )}
                        </div>
                    )
                })}
            </Tabs>
            {categoriesForSelect.length > 0 && (
                <FormItem htmlFor="category-parent" style={{ position: 'relative', marginTop: '1em' }}>
                    <ComboboxField
                        id="category-parent"
                        label={t('products.crud.categories.generalInfo.parentCategory')}
                        icon="PackageSearch"
                        fontWeight="regular"
                        options={categoriesForSelect}
                        loading={isLoadingCategoriesOrArticles}
                        value={newData.parentId}
                        onChange={onParentIdChange}
                    />
                </FormItem>
            )}
            <FormItem htmlFor="category-priority" style={{ position: 'relative', marginTop: '1em' }}>
                <FormItemName>{t('products.crud.categories.generalInfo.categoryPriority')}</FormItemName>
                <NumberInput
                    integerOnly
                    value={newData.priority === null ? '' : newData.priority}
                    min={1}
                    max={1000}
                    placeholder="1"
                    onChange={onPriorityChange}
                    onBlur={onPriorityBlur}
                />
            </FormItem>
            <Row style={{ marginTop: '1em' }}>
                <Col span={6}>
                    <FormItem htmlFor="category-booking-horizon">
                        <FormItemName>
                            {t('products.crud.categories.generalInfo.bookingHorizon')}
                            <Infotip pointer="left" maxWidth="28em">
                                {t('products.crud.categories.generalInfo.bookingHorizonTooltip')}
                                <div>{t('products.crud.categories.generalInfo.leaveBlankToDisable')}</div>
                            </Infotip>
                        </FormItemName>
                        <NumberInput
                            id="category-booking-horizon"
                            integerOnly
                            value={newData.bookingHorizon === null ? '' : newData.bookingHorizon}
                            min={1}
                            max={1000}
                            onChange={(event) => onBookingLimitsChange(event, 'bookingHorizon')}
                            placeholder="-"
                            status={showBookingLimitsError ? 'error' : 'normal'}
                        />
                    </FormItem>
                </Col>
                <Col span={6}>
                    <FormItem htmlFor="category-release-time">
                        <FormItemName>
                            {t('products.crud.categories.generalInfo.releaseTime')}
                            <Infotip pointer="left" maxWidth="30em">
                                {t('products.crud.categories.generalInfo.releaseTimeTooltip')}
                                <div>{t('products.crud.categories.generalInfo.leaveBlankToDisable')}</div>
                            </Infotip>
                        </FormItemName>
                        <NumberInput
                            id="category-release-time"
                            integerOnly
                            value={newData.releaseTime === null ? '' : newData.releaseTime}
                            min={1}
                            max={1000}
                            onChange={(event) => onBookingLimitsChange(event, 'releaseTime')}
                            placeholder="-"
                            status={showBookingLimitsError ? 'error' : 'normal'}
                        />
                    </FormItem>
                </Col>
                <ValidationMessage
                    className={showBookingLimitsError ? 'validation-message-visible' : ''}
                    style={{ top: '0.5em', marginBottom: '-2.5em' }}
                >
                    {t('products.crud.categories.generalInfo.bookingLimitsError')}
                </ValidationMessage>
            </Row>
            <FormItemName style={{ margin: '2em 0 1em 0' }}>
                <Trans
                    i18nKey="products.crud.categories.generalInfo.checkoutImageType"
                    values={{
                        imageShape:
                            newData.imageType === 'square_image'
                                ? t('products.crud.categories.generalInfo.imageShape.square')
                                : t('products.crud.categories.generalInfo.imageShape.wide'),
                    }}
                    components={{ strong: <strong /> }}
                />
                <Infotip pointer="left">{t('products.crud.categories.generalInfo.contactSupportToChange')}</Infotip>
            </FormItemName>
            <FormItem htmlFor="category-image" style={{ position: 'relative' }}>
                <FormItemName>{t('products.crud.categories.generalInfo.wideImage')}</FormItemName>
                <UploadInput
                    type="img"
                    fileSizeLimit={1}
                    onUpload={props.onWideImageUpload}
                    handleUploadSuccessResponse={props.onWideImageUploadSuccess}
                    inputId="category-image"
                    replaceTopMessages={props.replaceMessages}
                    removeAllMessages={props.removeAllMessages}
                />
                <UploadExplanation>
                    <Body size={3}>{t('products.crud.categories.generalInfo.wideImageSize')}</Body>
                </UploadExplanation>
            </FormItem>
            {!!newData.image && (
                <>
                    <FormItemName>{t('products.crud.categories.generalInfo.currentWideImageUrl')}</FormItemName>
                    <Row>
                        <ImageUrlCol span={9}>
                            <ImgLink href={newData.image} target="_blank">
                                {newData.image}
                            </ImgLink>
                        </ImageUrlCol>
                        <Col span={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <DeleteNote onClick={removeWideCategoryImage} style={{ margin: '0' }}>
                                <SecondaryText>x {t('common.remove')}</SecondaryText>
                            </DeleteNote>
                        </Col>
                    </Row>
                </>
            )}
            <FormItem htmlFor="category-square-image" style={{ position: 'relative', marginTop: '1em' }}>
                <FormItemName>{t('products.crud.categories.generalInfo.squareImage')}</FormItemName>
                <UploadInput
                    type="img"
                    fileSizeLimit={1}
                    onUpload={props.onSquareImageUpload}
                    handleUploadSuccessResponse={props.onSquareImageUploadSuccess}
                    parseErrorMessage
                    inputId="category-square-image"
                    replaceTopMessages={props.replaceMessages}
                    removeAllMessages={props.removeAllMessages}
                />
                <UploadExplanation>
                    <Body size={3}>{t('products.crud.categories.generalInfo.squareImageSize')}</Body>
                </UploadExplanation>
            </FormItem>
            {!!newData.squareImage && (
                <>
                    <FormItemName>{t('products.crud.categories.generalInfo.currentSquareImageUrl')}</FormItemName>
                    <Row>
                        <ImageUrlCol span={9}>
                            <ImgLink href={newData.squareImage} target="_blank">
                                {newData.squareImage}
                            </ImgLink>
                        </ImageUrlCol>
                        <Col span={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <DeleteNote onClick={removeSquareCategoryImage} style={{ margin: '0' }}>
                                <SecondaryText>x {t('common.remove')}</SecondaryText>
                            </DeleteNote>
                        </Col>
                    </Row>
                </>
            )}
            <FormTogglerCol span={12} style={{ marginTop: '2.5em' }}>
                <FormItemName>
                    {t('products.crud.categories.generalInfo.expandByDefault')}
                    <Infotip pointer="left" maxWidth="28em">
                        {t('products.crud.categories.generalInfo.expandByDefaultTooltip')}
                    </Infotip>
                </FormItemName>
                <FormToggler
                    id="card-open-by-default"
                    isOn={newData.cardOpenByDefault}
                    onClick={onCardOpenChange}
                    title={t('products.crud.categories.generalInfo.expandByDefault')}
                />
            </FormTogglerCol>
        </div>
    )
}
